/**
 * Tenant Billing - Vue de l'abonnement du tenant
 * Affiche un résumé de l'abonnement avec lien vers la gestion complète
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CreditCard,
  Package,
  Calendar,
  TrendingUp,
  ExternalLink,
  CheckCircle,
  AlertTriangle,
  Zap,
} from 'lucide-react';
import { Button, Spinner, Badge } from '@/components/ui-v2';

import { useTranslation } from 'react-i18next';
export const TenantBilling: React.FC = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [isLoading] = useState(false);
  const [error] = useState<Error | null>(null);

  // Utiliser les infos du user pour le plan (simplifié)
  // Note: UserWithTenant n'a pas de propriété 'tenant', seulement 'tenantId'
  // On utilise une valeur par défaut car le plan n'est pas disponible sur l'objet user
  const subscription = {
    plan: { name: 'Pro' },
    status: 'active',
    currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
  };

  const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
    border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl
    transition-all duration-300`;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50/50 backdrop-blur-sm border border-red-200/50 rounded-2xl p-6">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-red-500" />
          <div>
            <h3 className="font-semibold text-red-900">{t('admin.tenantBilling.loadError')}</h3>
            <p className="text-sm text-red-700">{t('admin.tenantBilling.loadErrorDesc')}</p>
          </div>
        </div>
      </div>
    );
  }

  const planName = subscription?.plan?.name || 'Free';
  const status = subscription?.status || 'active';
  const periodEnd = subscription?.currentPeriodEnd
    ? new Date(subscription.currentPeriodEnd).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '-';

  const getStatusBadge = () => {
    switch (status) {
      case 'active':
        return <Badge variant="success">{t('admin.tenantBilling.active')}</Badge>;
      case 'trialing':
        return <Badge variant="info">{t('admin.tenantBilling.trial')}</Badge>;
      case 'past_due':
        return <Badge variant="warning">{t('admin.tenantBilling.pastDue')}</Badge>;
      case 'canceled':
        return <Badge variant="alert">{t('admin.tenantBilling.canceled')}</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-teal-100 rounded-2xl">
            <CreditCard className="w-8 h-8 text-teal-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t('admin.tenantBilling.title')}</h2>
            <p className="text-gray-500">{t('admin.tenantBilling.subtitle')}</p>
          </div>
        </div>

        <Button
          variant="secondary"
          onClick={() => navigate('/pricing')}
          icon={<ExternalLink className="w-4 h-4" />}
        >
          {t('admin.tenantBilling.manage')}
        </Button>
      </div>

      {/* Plan actuel */}
      <div className={cardClasses}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="p-4 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl">
              <Package className="w-8 h-8 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h3 className="text-2xl font-bold text-gray-900">{planName}</h3>
                {getStatusBadge()}
              </div>
              <p className="text-gray-500 mt-1">{t('admin.tenantBilling.currentPlan')}</p>
            </div>
          </div>

          {planName !== 'Enterprise' && (
            <Button
              variant="secondary"
              onClick={() => navigate('/pricing')}
              icon={<Zap className="w-4 h-4" />}
            >
              {t('admin.tenantBilling.upgrade')}
            </Button>
          )}
        </div>

        {/* Période de facturation */}
        <div className="mt-6 pt-6 border-t border-gray-200/50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">{t('admin.tenantBilling.nextBilling')}</p>
                <p className="font-semibold text-gray-900">{periodEnd}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-teal-500" />
              <div>
                <p className="text-sm text-gray-500">{t('admin.tenantBilling.paymentStatus')}</p>
                <p className="font-semibold text-gray-900">{t('admin.tenantBilling.paymentUpToDate')}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <TrendingUp className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">{t('admin.tenantBilling.billingType')}</p>
                <p className="font-semibold text-gray-900">{t('admin.tenantBilling.monthly')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fonctionnalités incluses */}
      <div className={cardClasses}>
        <h4 className="font-semibold text-gray-900 mb-4">{t('admin.tenantBilling.includedFeatures')}</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            t('admin.tenantBilling.unlimitedInvoices'),
            t('admin.tenantBilling.unlimitedQuotes'),
            t('admin.tenantBilling.unlimitedClients'),
            t('admin.tenantBilling.accountingExport'),
            t('admin.tenantBilling.emailSupport'),
            t('admin.tenantBilling.legalArchiving'),
          ].map((feature, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-teal-500" />
              <span className="text-sm text-gray-700">{feature}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Actions rapides */}
      <div className="flex flex-wrap gap-3">
        <Button variant="ghost" onClick={() => navigate('/pricing')}>
          {t('admin.tenantBilling.viewAllPlans')}
        </Button>
        <Button variant="ghost" onClick={() => navigate('/invoices?type=platform')}>
          {t('admin.tenantBilling.invoiceHistory')}
        </Button>
        <Button variant="ghost" onClick={() => window.open('mailto:support@devis-pro.fr', '_blank')}>
          {t('admin.tenantBilling.contactSupport')}
        </Button>
      </div>
    </div>
  );
};

export default TenantBilling;
